import axios from 'axios'
import router from '@/router'
import { useAppShell } from '@/composables/appShell'
import { useSnackbarStore } from '../store/snackbar'
import { clearAuthSession } from '@/utils/session'
import { fetchAuthSession } from 'aws-amplify/auth'

const API_URL = import.meta.env.VITE_API
const VIA_CEP = 'https://viacep.com.br/ws'

const api = axios.create({
  headers: {
    'Content-Type': 'application/json'
  },
  baseURL: API_URL
})

api.interceptors.request.use(
  (config) => {
    const auth = localStorage.getItem('auth')
    if (auth && config.headers) {
      const session = JSON.parse(auth)
      config.headers.authorization = `Bearer ${session.AccessToken}`
      config.headers.user = session.id
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const getNewToken = async (refreshToken: string) => {
  return api.get('/users/auth', {
    headers: {
      RefreshToken: refreshToken
    }
  })
}

const redirectToLogin = () => {
  const appShell = useAppShell()
  const snackbarStore = useSnackbarStore()
  snackbarStore.showSnackbar({
    text: 'Tempo de sessão expirado',
    color: 'warning',
    timeout: 4000
  })

  appShell.setLoading(true, 'Redirecionando para o login...')
  clearAuthSession()
  router.replace({ name: 'Login' })
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      const auth = localStorage.getItem('auth')
      if (!auth) return redirectToLogin()

      const refreshToken = JSON.parse(auth)?.RefreshToken
      if (!refreshToken) return redirectToLogin()

      try {
        const response = await getNewToken(refreshToken)
        if (response.status !== 200) return redirectToLogin()

        const newAuth = {
          ...JSON.parse(auth),
          AccessToken: response.data.AccessToken
        }
        localStorage.setItem('auth', JSON.stringify(newAuth))

        error.config.headers['Authorization'] =
          `Bearer ${response.data.AccessToken}`
        return axios(error.config)
      } catch (err) {
        console.error('Failed to refresh token', err)
        redirectToLogin()
        return Promise.reject(err)
      }
    }

    return Promise.reject(error)
  }
)

export const viacepAPI = axios.create({
  headers: {
    'Content-Type': 'application/json'
  },
  baseURL: VIA_CEP
})

const getBlobErrorMessage = async (blob: Blob) => {
  try {
    const errorText = await blob.text()
    const errorData = JSON.parse(errorText)
    return errorData.message || errorData.error
  } catch {
    return null
  }
}

export const downloadZip = async (
  diligenceId: string,
  entityId?: string
): Promise<Blob> => {
  const hostname = new URL(API_URL).hostname

  let token = ''
  try {
    const session = await fetchAuthSession()
    const accessToken = session.tokens?.accessToken?.toString()
    if (accessToken) {
      token = accessToken

      const auth = localStorage.getItem('auth')
      if (auth) {
        const parsedAuth = JSON.parse(auth)
        if (parsedAuth.AccessToken !== accessToken) {
          parsedAuth.AccessToken = accessToken
          localStorage.setItem('auth', JSON.stringify(parsedAuth))
        }
      }
    }
  } catch (error) {
    console.error('Error refreshing Amplify session in downloadZip:', error)
  }

  if (!token) {
    const auth = localStorage.getItem('auth')
    token = auth ? JSON.parse(auth).AccessToken : ''
  }

  const response = await axios
    .get(`https://zip-${hostname}/${diligenceId}`, {
      responseType: 'blob',
      headers: { Authorization: `Bearer ${token}` },
      params: entityId ? { entityId } : {},
      validateStatus: () => true
    })
    .catch((error: any) => {
      if (
        error.code === 'ERR_NETWORK' ||
        error.message?.includes('Network Error') ||
        !error.response
      ) {
        console.warn(
          'Axios CORS/Network error no downloadZip. Executando fallback de download nativo via navegador...',
          error
        )
        const downloadUrl = `https://zip-${hostname}/${diligenceId}?token=${encodeURIComponent(
          token
        )}${entityId ? `&entityId=${entityId}` : ''}`
        const link = document.createElement('a')
        link.href = downloadUrl
        link.target = '_blank'
        link.rel = 'noopener noreferrer'
        document.body.appendChild(link)
        link.click()
        link.remove()
        return {
          data: new Blob([], { type: 'application/zip' }),
          headers: { 'content-type': 'application/zip' },
          status: 200
        }
      }
      throw error
    })

  const contentType: string = response.headers['content-type'] ?? ''
  if (response.status >= 400) {
    if (contentType.includes('json')) {
      const errorMessage = await getBlobErrorMessage(response.data)
      throw new Error(errorMessage || 'Falha ao baixar documentos.')
    }

    throw new Error(`Falha ao baixar documentos. Status ${response.status}.`)
  }

  if (!contentType.includes('zip') && !contentType.includes('octet-stream')) {
    if (contentType.includes('json')) {
      const errorMessage = await getBlobErrorMessage(response.data)
      throw new Error(errorMessage || 'Falha ao baixar documentos.')
    }

    throw new Error('Resposta inválida ao baixar documentos.')
  }

  return response.data
}

export default api
